import React, { useEffect, useState, useRef } from "react";
import {
    Typography,
    Card,
    CardHeader,
    CardBody,
    Button,
    Input,
    IconButton,
} from "@material-tailwind/react";
import Swal from "sweetalert2";
import { ArrowDownTrayIcon, BanknotesIcon, EllipsisVerticalIcon, MagnifyingGlassIcon, PencilSquareIcon, PlusCircleIcon, TrashIcon, XCircleIcon } from "@heroicons/react/24/solid";
import AddMainCategory from "./main-category-modal";
import AddCategory from "./category-modal";
import AddSubCategory from "./sub-category-modal";
import { useSelector, useDispatch } from "react-redux";
import { getMainCategory, getCategory, getSubCategory, deleteCategory, featuredCategory, downloadCategoriesCSV, resetStatus } from "@/store/slices/categorySlice";
import { Popover } from 'react-tiny-popover';
import { setSelectedCategory } from '@/store/slices/categorySlice';
import ShowLoader from "@/components/Loader";
import Toaster from "../../components/Toaster";
import { StarIcon as StarSolidIcon } from "@heroicons/react/24/solid";
import { StarIcon as StarOutlineIcon } from "@heroicons/react/24/outline";
import useHasPermission from "../../components/useHasPermission";
import ScrollToTop from "@/components/ScrollToTop";
import { handleDownloadURL } from "@/components/DownloadFile";
import { useTranslation } from "react-i18next";


export function Category() {
    const { t } = useTranslation("category");
    const dispatch = useDispatch();

    const [isOpenMainCategoryModal, setIsOpenMainCategoryModal] = useState(false);
    const [isOpenSubCategoryModal, setIsOpenSubCategoryModal] = useState(false);
    const [isOpenCategoryModal, setIsOpenCategoryModal] = useState(false);
    const handelOpenMainCategoryModal = () => setIsOpenMainCategoryModal((prev) => !prev);
    const handelOpenSubCategoryModal = () => setIsOpenSubCategoryModal((prev) => !prev);
    const handelOpenCategoryModal = () => setIsOpenCategoryModal((prev) => !prev);
    const hasAddPermission = useHasPermission(`category.add`);
    const hasViewPermission = useHasPermission(`category.view`);
    const hasUpdatePermission = useHasPermission(`category.update`);
    const hasDeletePermission = useHasPermission(`category.delete`);

    const [search, setSearch] = useState('');

    const [selectedMainCategotyId, setSelectedMainCategoryId] = useState('');
    const [selectedCategotyId, setSelectedCategoryId] = useState('');

    const { list_main_categories, list_categories, list_sub_categories, loading } = useSelector((state) => state.category);

    const getAllCategory = (search) => {
        dispatch(getMainCategory(search))
        .unwrap()
        .then((result) => {
            setSelectedMainCategoryId(result?.main_category[0]?.id);
            return dispatch(getCategory(result?.main_category[0]?.id)).unwrap();
        })
        .then((res) => {
            setSelectedCategoryId(res[0]?.id);
            return dispatch(getSubCategory(res[0]?.id));
        })
        .catch((error) => {
            console.error("Error while fetching categories:", error);
        });
    }

    useEffect(() => {
        getAllCategory(search);
    }, [dispatch]);

    const mainCategoryOnClick = (main_category_id) => {
        setSelectedMainCategoryId(main_category_id);
        dispatch(getCategory(main_category_id))
            .unwrap()
            .then((res) => {
                setSelectedCategoryId(res[0]?.id);
                return dispatch(getSubCategory(res[0]?.id));
            });
    }

    const categoryOnClick = (category_id) => {
        setSelectedCategoryId(category_id);
        dispatch(getSubCategory(category_id));
    }

    const [isPopoverOpen, setIsPopoverOpen] = useState([]);
    const openSubCategoryPopover = (id, type) => {
        const key = `${id}-${type}`;
        setIsPopoverOpen((prev) => {
            if (prev.includes(key)) {
                return [];
            } else {
                return [key];
            }
        });
    };

    const handelSearch = (event) => {
        const value = event.target.value;

        if (event.key === "Enter" && value) {
            setSelectedCategoryId('');
            getAllCategory(value);
        } else if (event.key === "Backspace" && value.length === 0) {
            setSelectedCategoryId('');
            getAllCategory('');
        }
    }

    const handelRemoveSearch = () => {
        setSearch('');
        getAllCategory('');
    }
    const handleSearchClick = () => {
        getAllCategory(search);
    }

    const handelEdit = (id, type) => {
        if (id) {
            let category = '';
            if (type === 'main_category') {
                category = list_main_categories?.main_category?.find((row) => String(row.id) === String(id));
                handelOpenMainCategoryModal();
            } else if (type === 'sub_category_1') {
                category = list_categories?.find((row) => String(row.id) === String(id));
                handelOpenCategoryModal();
            } else {
                category = list_sub_categories?.find((row) => String(row.id) === String(id));
                handelOpenSubCategoryModal();
            }
            dispatch(setSelectedCategory(category));
        }
    }

    const handelDelete = (id, type) => {
        Swal.fire({
            title: t('are_you_sure'),
            text: t('want_to_delete'),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: t('yes_delete_it'),
            cancelButtonText: t('cancel')
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteCategory({ id: id, type: type })).unwrap().then(() => {
                    Toaster.success(t('category_deleted_successfully'));
                    getAllCategory(search);
                }).catch((error) => {
                    console.error("Delete failed:", error);
                    Toaster.error(t('delete_failed'));
                });
            }
        });
    }

    const isFeatured = (id, type) => {
        let msg = type === 'yes' ? 'featured' : 'unfeatured';
        let text = type === 'yes' ? t('want_to_feature') : t('want_to_unfeature');
        Swal.fire({
            title: t('are_you_sure'),
            text: `${text}`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: t('yes')
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(featuredCategory({ id: id, type: type })).unwrap().then(() => {
                    let success_msg = type === 'yes' ? t('category_featured_successfully') : t('category_unfeatured_successfully');
                    Toaster.success(success_msg);
                    getAllCategory(search);
                }).catch((error) => {
                    console.error("Changes failed:", error);
                    Toaster.error(t('changes_failed'));
                });
            }
        });
    }

    const popoverRef = useRef(null);
    useEffect(() => {
        function handleClickOutside(e) {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) {
                setIsPopoverOpen([]);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const downloadCategories = async () => {
        try {
            const result = await dispatch(downloadCategoriesCSV()).unwrap();
            if (result?.status) {
                handleDownloadURL({ fileUrl: result?.data?.csv_url });
                Toaster.success(t('categories_downloaded_successfully'));
                dispatch(resetStatus());
            } else {
                Toaster.error(result.message || t('something_went_wrong'));
                dispatch(resetStatus());
            }
        } catch (err) {
            Toaster.error(err?.message || t('failed_to_download'));
            dispatch(resetStatus());
        }
    }

    return <div className="mt-5 mb-8 flex flex-col gap-8">
        {loading && <ShowLoader />}
        <ScrollToTop />
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-0 px-4 md:px-6">
            <div className="w-full md:flex-1">
                <Typography variant="h5" color="blue-gray">
                    {t('category_management')}
                </Typography>
            </div>
            <div className="w-full md:flex-1 flex justify-end">
                <div className="w-full max-w-md relative">
                    <Input className={search ? 'pr-10' : ''}  label={t('search')} icon={!search ? <MagnifyingGlassIcon className="h-5 w-5" /> : ''} value={search} onChange={(e) => setSearch(e.target.value)} onKeyUp={handelSearch} />
                    {search && (
                        <IconButton variant="text" size="sm" className="!absolute right-1 top-1" onClick={handelRemoveSearch}>
                            <XCircleIcon className="h-5 w-5 text-red-500" />
                        </IconButton>
                    )}
                </div>
                {search && (<Button variant="outlined" className="flex items-center border-gray-300 capitalize font-bold bg-black text-white mx-2"
                    onClick={handleSearchClick}
                ><MagnifyingGlassIcon className="w-4 h-4 mr-1" /> {t('search')}</Button>)}
                
            </div>
            <div className="w-full md:flex-1 flex justify-end">
                <Button
                    variant="outlined"
                    className="flex items-center gap-2 border-gray-300 capitalize"
                    onClick={() => downloadCategories()}
                >
                    <ArrowDownTrayIcon className="h-4 w-4" />

                    {t('download')}
                </Button>
            </div>
        </div>
        <div className="mb-7 grid gap-y-10 gap-x-6 md:grid-cols-2 xl:grid-cols-4 px-4 md:px-6">
            <div className="border border-blue-gray-100 shadow-sm p-4 rounded-xl bg-white">
                <Typography variant="small" className="font-normal text-blue-gray-600">
                    {t('total_main_categories')}
                </Typography>
                <Typography variant="h4" color="blue-gray" className="mt-4">
                    {list_main_categories?.data_count?.totalMainCategories ?? 0}
                </Typography>
            </div>
            <div className="border border-blue-gray-100 shadow-sm p-4 rounded-xl bg-white">
                <Typography variant="small" className="font-normal text-blue-gray-600">
                    {t('total_categories')}
                </Typography>
                <Typography variant="h4" color="blue-gray" className="mt-4">
                    {list_main_categories?.data_count?.totalCategories ?? 0}
                </Typography>
            </div>
            <div className="border border-blue-gray-100 shadow-sm p-4 rounded-xl bg-white">
                <Typography variant="small" className="font-normal text-blue-gray-600">
                    {t('total_sub_categories')}
                </Typography>
                <Typography variant="h4" color="blue-gray" className="mt-4">
                    {list_main_categories?.data_count?.totalSubCategories ?? 0}
                </Typography>
            </div>
        </div>
        <div className="px-4 md:px-6">
            <Typography variant="h6" color="blue-gray" className="font-medium">{t('manage_your_category_effectively')}</Typography>
        </div>
        <div className="mb-7 grid gap-y-10 gap-x-2 md:grid-cols-2 xl:grid-cols-3 overflow-x-auto px-4 md:px-6">
            <div className="border border-blue-gray-100 shadow-sm p-4 rounded-xl bg-white">
                <div className="m-0 p-4 flex justify-between">
                    <Typography variant="h6" color="blue-gray">
                        {t('main_category')}
                    </Typography>
                    <Typography variant="small" color="gray" className="-mb-3 font-medium">
                        {list_main_categories?.main_category?.length || 0} {t('total')}
                    </Typography>
                </div>
                <div className="px-0 pt-0 pb-2">
                    <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-y-auto max-h-[500px] scrollbar-hide">
                        <ul className="flex flex-col gap-2 px-1">
                            {!list_main_categories?.main_category?.length ?
                                <li key='0' className="flex items-center justify-between p-3 rounded-xl hover:bg-blue-50 cursor-pointer transition">{t('no_data_found')}</li>
                                :
                                list_main_categories?.main_category?.map((cat, index) => (
                                    <li onClick={() => {
                                        mainCategoryOnClick(cat.id);
                                    }}
                                        key={index}
                                        className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition ${selectedMainCategotyId == cat.id ? 'bg-blue-50' : 'hover:bg-blue-50'}`}
                                    >
                                        <div>
                                            <h3 className="text-sm font-medium text-gray-800 flex items-center">
                                                {cat.is_featured === 'yes' ?
                                                    <StarSolidIcon className="w-5 h-5 text-green-500 cursor-pointer mr-1" onClick={() => isFeatured(cat.id, 'no')} />
                                                    :
                                                    <StarOutlineIcon className="w-5 h-5 text-gray-500 cursor-pointer mr-1" onClick={() => isFeatured(cat.id, 'yes')} />
                                                }
                                                {cat.name}
                                            </h3>
                                            <p className="text-xs text-gray-500 ml-6">
                                                {cat?.categories_count ?? 0} {t('categories')}
                                            </p>
                                        </div>
                                        {(hasUpdatePermission || hasDeletePermission) &&
                                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                                <span> {cat.products_count ?? 0} {t('items')}</span>
                                                <Popover
                                                    isOpen={isPopoverOpen.includes(`${cat.id}-maincategory`)}
                                                    positions={['bottom', 'left']}
                                                    padding={4}
                                                    content={<div ref={popoverRef} className="bg-white shadow-xl rounded-md w-32 flex flex-col z-50 border border-gray-100 py-1">
                                                        {hasUpdatePermission && <button onClick={() => handelEdit(cat.id, "main_category")} className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2">
                                                            <PencilSquareIcon className="h-4 w-4 text-blue-500" />
                                                            {t('edit')}
                                                        </button>}
                                                        {hasDeletePermission && <button onClick={() => handelDelete(cat.id, "main_category")} className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2">
                                                            <TrashIcon className="h-4 w-4 text-red-500" />
                                                            {t('delete')}
                                                        </button>}
                                                    </div>
                                                    }
                                                >
                                                    <IconButton variant="text" color="blue-gray" onClick={() => openSubCategoryPopover(cat.id, "maincategory")}>
                                                        <EllipsisVerticalIcon className="h-6 w-6" />
                                                    </IconButton>
                                                </Popover>
                                            </div>
                                        }
                                    </li>
                                ))
                            }
                        </ul>

                    </div>
                </div>
                {hasAddPermission &&
                    <div className="flex flex-col gap-2 px-1">
                        <Button
                        className={`
                            m-6 flex items-center justify-center gap-2 
                            bg-green-900 text-white font-bold uppercase tracking-wider
                            transition-all duration-200 ease-in-out
                            shadow-md active:shadow-sm
                            
                            /* The "Perfect" Hover Logic */
                            hover:-translate-y-1 hover:scale-105 
                            hover:bg-white hover:text-green-900 hover:shadow-xl
                            
                            /* Active/Pressed State */
                            active:translate-y-0 active:scale-95
                        `}
                            // className="m-6 success flex items-center justify-center gap-2"
                            onClick={handelOpenMainCategoryModal}
                        >
                            <PlusCircleIcon className="h-4 w-4" />
                            <span>{t('new_category')}</span>
                        </Button>
                    </div>
                }
            </div>
            <div className="border border-blue-gray-100 shadow-sm p-4 rounded-xl bg-white">
                <div className="m-0 p-4 flex justify-between">
                    <Typography variant="h6" color="blue-gray">
                        {t('category')}
                    </Typography>
                    <Typography variant="small" color="gray" className="-mb-3 font-medium">
                        {list_categories?.length || 0} {t('total')}
                    </Typography>
                </div>
                <div className="px-0 pt-0 pb-2">
                    <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-y-auto max-h-[500px] scrollbar-hide">
                        <ul className="flex flex-col gap-2 px-1">
                            {!list_categories?.length ?
                                <li key='0' className="flex items-center justify-between p-3 rounded-xl hover:bg-blue-50 cursor-pointer transition">{t('no_data_found')}</li>
                                :
                                list_categories?.map((cat, index) => (
                                    <li onClick={() => {
                                        categoryOnClick(cat.id);
                                    }}
                                        key={index}
                                        className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition ${selectedCategotyId == cat.id ? 'bg-blue-50' : 'hover:bg-blue-50'}`} >
                                        <div>
                                            <h3 className="text-sm font-medium text-gray-800">
                                                {cat?.name}
                                            </h3>
                                            <p className="text-xs text-gray-500">
                                                {cat?.categories_count ?? 0} {t('sub_categories')}
                                            </p>
                                        </div>

                                        {(hasUpdatePermission || hasDeletePermission) &&
                                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                                <span>{cat.products_count} {t('items')}</span>
                                                <Popover
                                                    isOpen={isPopoverOpen.includes(`${cat.id}-category`)}
                                                    positions={['bottom', 'left']}
                                                    padding={4}
                                                    content={<div ref={popoverRef} className="bg-white shadow-xl rounded-md w-32 flex flex-col z-50 border border-gray-100 py-1">
                                                        {hasUpdatePermission && <button onClick={() => handelEdit(cat.id, "sub_category_1")} className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2">
                                                            <PencilSquareIcon className="h-4 w-4 text-blue-500" />
                                                            {t('edit')}
                                                        </button>}
                                                        {hasDeletePermission && <button onClick={() => handelDelete(cat.id, "sub_category_1")} className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2">
                                                            <TrashIcon className="h-4 w-4 text-red-500" />
                                                            {t('delete')}
                                                        </button>}
                                                    </div>
                                                    }
                                                >
                                                    <IconButton variant="text" color="blue-gray" onClick={() => openSubCategoryPopover(cat.id, "category")}>
                                                        <EllipsisVerticalIcon className="h-6 w-6" />
                                                    </IconButton>
                                                </Popover>
                                            </div>
                                        }
                                    </li>
                                ))
                            }
                        </ul>
                    </div>
                </div>
                {hasAddPermission &&
                    <div className="flex flex-col gap-2 px-1">
                        <Button
                            // className="m-6 success flex items-center justify-center gap-2"
                               className={`
                            m-6 flex items-center justify-center gap-2 
                            bg-green-900 text-white font-bold uppercase tracking-wider
                            transition-all duration-200 ease-in-out
                            shadow-md active:shadow-sm
                            
                            /* The "Perfect" Hover Logic */
                            hover:-translate-y-1 hover:scale-105 
                            hover:bg-white hover:text-green-900 hover:shadow-xl
                            
                            /* Active/Pressed State */
                            active:translate-y-0 active:scale-95
                        `}
                            onClick={handelOpenCategoryModal}
                        >
                            <PlusCircleIcon className="h-4 w-4" />
                            <span>{t('new_category')}</span>
                        </Button>
                    </div>
                }
            </div>
            <div className="border border-blue-gray-100 shadow-sm p-4 rounded-xl bg-white">
                <div className="m-0 p-4 flex justify-between">
                    <Typography variant="h6" color="blue-gray">
                        {t('sub_category')}
                    </Typography>
                    <Typography variant="small" color="gray" className="-mb-3 font-medium">
                        {list_sub_categories?.length || 0} {t('total')}
                    </Typography>
                </div>
                <div className="px-0 pt-0 pb-2">
                    <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-y-auto max-h-[500px] scrollbar-hide">
                        <ul className="flex flex-col gap-2 px-1">
                            {!list_sub_categories?.length ?
                                <li key='0' className="flex items-center justify-between p-3 rounded-xl hover:bg-blue-50 cursor-pointer transition">{t('no_data_found')}</li>
                                :
                                list_sub_categories?.map((cat, index) => (
                                    <li
                                        key={index}
                                        className="flex items-center justify-between p-3 rounded-xl hover:bg-blue-50 cursor-pointer transition"
                                    >
                                        <div>
                                            <h3 className="text-sm font-medium text-gray-800">
                                                {cat?.name}
                                            </h3>
                                        </div>
                                        {(hasUpdatePermission || hasDeletePermission) &&
                                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                                <span>{cat.products_count} {t('items')}</span>
                                                <Popover
                                                    isOpen={isPopoverOpen.includes(`${cat.id}-subcategory`)}
                                                    positions={['bottom', 'left']}
                                                    padding={4}
                                                    content={<div ref={popoverRef} className="bg-white shadow-xl rounded-md w-32 flex flex-col z-50 border border-gray-100 py-1">
                                                        {hasUpdatePermission && <button onClick={() => handelEdit(cat.id, "sub_category_2")} className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2">
                                                            <PencilSquareIcon className="h-4 w-4 text-blue-500" />
                                                            {t('edit')}
                                                        </button>}
                                                        {hasDeletePermission && <button onClick={() => handelDelete(cat.id, "sub_category_2")} className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2">
                                                            <TrashIcon className="h-4 w-4 text-red-500" />
                                                            {t('delete')}
                                                        </button>}
                                                    </div>
                                                    }
                                                >
                                                    <IconButton variant="text" color="blue-gray" onClick={() => openSubCategoryPopover(cat.id, "subcategory")}>
                                                        <EllipsisVerticalIcon className="h-6 w-6" />
                                                    </IconButton>
                                                </Popover>
                                            </div>
                                        }
                                    </li>
                                ))
                            }
                        </ul>

                    </div>
                </div>
                {hasAddPermission && (
                    <div className="flex flex-col gap-2 px-1">
                        <Button
                            // className="m-6 success flex items-center justify-center gap-2"
                               className={`
                            m-6 flex items-center justify-center gap-2 
                            bg-green-900 text-white font-bold uppercase tracking-wider
                            transition-all duration-200 ease-in-out
                            shadow-md active:shadow-sm
                            
                            /* The "Perfect" Hover Logic */
                            hover:-translate-y-1 hover:scale-105 
                            hover:bg-white hover:text-green-900 hover:shadow-xl
                            
                            /* Active/Pressed State */
                            active:translate-y-0 active:scale-95
                        `}
                            onClick={handelOpenSubCategoryModal}
                        >
                            <PlusCircleIcon className="h-4 w-4" />
                            <span>{t('new_category')}</span>
                        </Button>
                    </div>
                )}
            </div>
        </div>
        <AddMainCategory
            open={isOpenMainCategoryModal}
            handler={handelOpenMainCategoryModal}
            setSelectedMainCategoryId={setSelectedMainCategoryId}
            setSelectedCategoryId={setSelectedCategoryId}
        />

        <AddCategory
            open={isOpenCategoryModal}
            handler={handelOpenCategoryModal}
            parent_id={selectedMainCategotyId}
            setSelectedMainCategoryId={setSelectedMainCategoryId}
            setSelectedCategoryId={setSelectedCategoryId}
        />

        <AddSubCategory
            open={isOpenSubCategoryModal}
            handler={handelOpenSubCategoryModal}
            parent_id={selectedCategotyId}
            setSelectedCategoryId={setSelectedCategoryId}
        />
    </div>
}

export default Category;